// DRF `?ordering=` helpers: "field" = ascending, "-field" = descending.

export type SortDir = "asc" | "desc";
export type Ordering<F extends string> = F | `-${F}`;

export function orderingField(ordering: string | null | undefined): string | null {
  if (!ordering) return null;
  return ordering.startsWith("-") ? ordering.slice(1) : ordering;
}

export function orderingDir(ordering: string | null | undefined): SortDir {
  return ordering?.startsWith("-") ? "desc" : "asc";
}

/**
 * Next ordering after clicking `field`'s header: the same column flips direction, a new column
 * starts at `firstDir` (numbers/dates usually "desc" = biggest/latest first, text "asc").
 */
export function toggleOrdering<F extends string>(
  current: string | null | undefined,
  field: F,
  firstDir: SortDir = "desc",
): Ordering<F> {
  const dir = orderingField(current) === field ? (orderingDir(current) === "asc" ? "desc" : "asc") : firstDir;
  return dir === "desc" ? `-${field}` : field;
}

/**
 * Comparator for mock data mirroring the backend's NullsLastOrderingFilter: null/undefined values
 * sink to the bottom in BOTH directions; strings compare locale-aware, booleans as false < true.
 */
export function compareNullsLast(a: unknown, b: unknown, dir: SortDir): number {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull || bNull) return aNull === bNull ? 0 : aNull ? 1 : -1;
  let cmp: number;
  if (typeof a === "string" && typeof b === "string") {
    const na = Number(a);
    const nb = Number(b);
    cmp = a.trim() !== "" && b.trim() !== "" && Number.isFinite(na) && Number.isFinite(nb)
      ? na - nb
      : a.localeCompare(b);
  } else {
    cmp = Number(a) - Number(b);
  }
  return dir === "desc" ? -cmp : cmp;
}
